import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/src/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/src/components/ui/table';
import { useAdminOrg } from '../hooks/useAdminOrgs';
import { useAdminDemoStatus } from '../hooks/useAdminDemo';
import { PlanBadge } from '../components/PlanBadge';
import { PeriodEndCell } from '../components/PeriodEndCell';
import { formatDate, formatDateTime } from '../lib/adminFormat';
import { AssignPlanSheet } from './components/AssignPlanSheet';
import { SuspendOrgButton } from './components/SuspendOrgButton';
import { ResetDemoOrgButton } from './components/ResetDemoOrgButton';

export function OrgDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: org, isLoading, error } = useAdminOrg(id);
  const { data: demoStatus } = useAdminDemoStatus();
  const [assignPlanOpen, setAssignPlanOpen] = useState(false);

  if (isLoading) {
    return <p className="text-muted-foreground">Đang tải...</p>;
  }

  if (error || !org) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/orgs" className="inline-flex items-center gap-1 text-sm text-surface-600 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Quay lại danh sách tổ chức
        </Link>
        <p className="text-destructive">Không thể tải tổ chức này. Vui lòng thử lại sau.</p>
      </div>
    );
  }

  const suspended = !!org.suspendedAt;

  return (
    <div className="flex flex-col gap-6">
      <Link to="/orgs" className="inline-flex items-center gap-1 text-sm text-surface-600 hover:underline w-fit">
        <ArrowLeft className="h-4 w-4" /> Quay lại danh sách tổ chức
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-semibold tracking-tight text-surface-900">
              {org.name}
            </h1>
            {suspended && <Badge variant="destructive">Đã khoá</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{org.slug}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" onClick={() => setAssignPlanOpen(true)}>
            Gán gói
          </Button>
          {demoStatus?.enabled && demoStatus.slugs.includes(org.slug) && (
            <ResetDemoOrgButton slug={org.slug} orgName={org.name} />
          )}
          <SuspendOrgButton orgId={org.id} suspended={suspended} />
        </div>
      </div>

      <AssignPlanSheet
        orgId={org.id}
        open={assignPlanOpen}
        onOpenChange={setAssignPlanOpen}
        current={{ planCode: org.planCode, subStatus: org.subStatus, billingCycle: org.billingCycle }}
      />

      <Card>
        <CardHeader>
          <CardTitle className="font-display font-semibold tracking-tight text-surface-900">Gói</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-muted-foreground">Gói</dt>
              <dd className="mt-0.5 flex items-center gap-1.5">
                <PlanBadge planCode={org.planCode} />
                {/* Cycle rides in the same cell (the card is a full 4-column row
                    already). FREE has no cycle to bill against, so no chip. */}
                {org.billingCycle && org.planCode !== 'FREE' && (
                  <Badge variant="muted">{org.billingCycle === 'ANNUAL' ? 'Năm' : 'Tháng'}</Badge>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Trạng thái</dt>
              <dd className="mt-0.5">{org.subStatus ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Hạn</dt>
              <dd className="mt-0.5">
                <PeriodEndCell currentPeriodEnd={org.currentPeriodEnd} />
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Tự động gia hạn</dt>
              <dd className="mt-0.5">{org.autoRenew ? 'Có' : 'Không'}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-display font-semibold tracking-tight text-surface-900">
            Thành viên
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Tên</TableHead>
                <TableHead>Vai trò</TableHead>
                <TableHead>Ngày tham gia</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {org.members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                    Chưa có thành viên nào.
                  </TableCell>
                </TableRow>
              ) : (
                org.members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{member.fullName ?? '—'}</TableCell>
                    <TableCell>{member.role}</TableCell>
                    <TableCell className="tabular-nums text-surface-600">
                      {formatDate(member.joinedAt)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="font-display font-semibold tracking-tight text-surface-900">
            Audit gần đây
          </CardTitle>
          <Link to={`/audit?orgId=${org.id}`} className="text-sm text-surface-600 hover:underline">
            Xem tất cả
          </Link>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Thời gian</TableHead>
                <TableHead>Người thực hiện</TableHead>
                <TableHead>Hành động</TableHead>
                <TableHead>Đối tượng</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {org.recentAudit.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                    Chưa có bản ghi audit nào.
                  </TableCell>
                </TableRow>
              ) : (
                org.recentAudit.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap tabular-nums text-surface-600">
                      {formatDateTime(row.createdAt)}
                    </TableCell>
                    <TableCell>{row.actorEmail ?? '—'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{row.action}</Badge>
                    </TableCell>
                    <TableCell>{row.entityType}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
